import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Brand from "../components/Brand";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    organisation: "",
    traditional_owner_group: "",
    role: "project_partner",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(form);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background sk-contour p-8" data-testid="register-page">
      <form onSubmit={submit} className="w-full max-w-lg sk-fade-in bg-white border border-[#E6E2D8] rounded-md p-8 shadow-sm">
        <div className="mb-6">
          <Brand variant="inline" tone="default" size={44} subtitle="Create your account" />
        </div>

        <h2 className="font-display text-2xl font-bold tracking-tight text-[#2C2A28]">Join SeedKeepers</h2>
        <p className="text-[13px] text-[#5C5854] mt-1">For ranger groups, Traditional Owner organisations, and restoration teams.</p>

        <div className="space-y-4 mt-6">
          <div>
            <Label className="text-[11px] tracking-[0.18em] uppercase text-[#5C5854] font-semibold">Full name</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2 bg-white border-[#E6E2D8]" data-testid="register-name" />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.18em] uppercase text-[#5C5854] font-semibold">Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 bg-white border-[#E6E2D8]" data-testid="register-email" />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.18em] uppercase text-[#5C5854] font-semibold">Password</Label>
            <Input required type="password" minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-2 bg-white border-[#E6E2D8]" data-testid="register-password" />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.18em] uppercase text-[#5C5854] font-semibold">Organisation</Label>
            <Input value={form.organisation} onChange={(e) => set("organisation", e.target.value)} placeholder="Ranger group, NGO, university…" className="mt-2 bg-white border-[#E6E2D8]" data-testid="register-organisation" />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.18em] uppercase text-[#5C5854] font-semibold">Traditional Owner group</Label>
            <Input value={form.traditional_owner_group} onChange={(e) => set("traditional_owner_group", e.target.value)} placeholder="If applicable" className="mt-2 bg-white border-[#E6E2D8]" data-testid="register-to-group" />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.18em] uppercase text-[#5C5854] font-semibold">Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="mt-2 bg-white border-[#E6E2D8]" data-testid="register-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="project_partner">Project Partner</SelectItem>
                <SelectItem value="to_admin">Traditional Owner Admin</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#8A857D] mt-1">System admins are provisioned separately.</p>
          </div>

          {error && <div className="text-[12px] text-[#B45B3E]" data-testid="register-error">{error}</div>}

          <Button type="submit" disabled={loading} className="w-full bg-[#8B5A2B] hover:bg-[#6B4420] text-[#FDFBF7]" data-testid="register-submit">
            {loading ? "Creating…" : "Create account"}
          </Button>

          <p className="text-[13px] text-[#5C5854]">
            Already have an account? <Link to="/login" className="text-[#8B5A2B] font-semibold underline-offset-4 hover:underline" data-testid="link-login">Sign in</Link>
          </p>
        </div>
      </form>
    </div>
  );
}
